import {
  getDocs, addDoc, updateDoc, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { functions, httpsCallable } from '../firebase';
import { getEffectiveTenantId, tenantCollection, tenantDoc } from './tenantContext';
import { logConfigAudit } from './auditService';

const COL = 'surveyCampaigns';

export async function getSurveyCampaigns(tenantId) {
  const tid = tenantId || await getEffectiveTenantId();
  const snap = await getDocs(query(tenantCollection(tid, COL), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createSurveyCampaign(tenantId, data, userId) {
  const tid = tenantId || await getEffectiveTenantId();
  const payload = {
    pathwayId: data.pathwayId,
    pathwayName: data.pathwayName || '',
    anchorMonth: data.anchorMonth,
    cohortCount: 3,
    minRpmActiveMonths: 2,
    expireAfterDays: 30,
    active: false,
    createdBy: userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(tenantCollection(tid, COL), payload);
  await logConfigAudit({
    category: 'pathway',
    action: 'survey_campaign_created',
    entity: 'surveyCampaign',
    entityId: ref.id,
    tenantId: tid,
    details: { pathwayId: payload.pathwayId, pathwayName: payload.pathwayName, anchorMonth: payload.anchorMonth },
  });
  return ref.id;
}

export async function setSurveyCampaignActive(tenantId, campaign, active) {
  const tid = tenantId || await getEffectiveTenantId();
  await updateDoc(tenantDoc(tid, COL, campaign.id), { active: !!active, updatedAt: serverTimestamp() });
  await logConfigAudit({
    category: 'pathway',
    action: active ? 'survey_campaign_activated' : 'survey_campaign_deactivated',
    entity: 'surveyCampaign',
    entityId: campaign.id,
    tenantId: tid,
    details: { pathwayId: campaign.pathwayId, pathwayName: campaign.pathwayName },
  });
}

async function callWithTenant(name, payload, timeout) {
  if (!functions) throw new Error('Functions not initialized.');
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('No tenant context.');
  const fn = httpsCallable(functions, name, timeout ? { timeout } : undefined);
  const res = await fn({ tenantId, ...payload });
  return res.data;
}

/** Send this month's cohort now (idempotent on the server). */
export function runSurveyCampaignNow(campaignId) {
  return callWithTenant('runSurveyCampaignNow', { campaignId }, 540000);
}

/** De-identified aggregates for the PRO Summary report. */
export function fetchProSummary({ pathwayId, month, clientId }) {
  return callWithTenant('getProSummary', { pathwayId, month, clientId: clientId || null }, 120000);
}

/** Recent positive survey comments for the signed-in user's care-managed patients. */
export function fetchCareManagerSurveyBoost() {
  return callWithTenant('getCareManagerSurveyBoost', {}, 60000);
}

/** Identified responses for the internal PRO Detail report (audit-logged on the server). */
export function fetchProInternalDetail({ pathwayId, month, clientId, range, purpose = 'view' }) {
  return callWithTenant('getProInternalDetail', {
    pathwayId, month, clientId: clientId || null, range, purpose,
  }, 120000);
}
